import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  getMixedContent,
  initVersionedSchema,
  MixedSchema,
  NodeSchema,
  TextFormatting,
  toParagraphContent,
  toTextContent,
} from '@principle-theorem/editor';
import { BlockNodes } from '@principle-theorem/editor';
import {
  Referral,
  ReferralInteraction,
} from '@principle-theorem/principle-core';
import {
  INTERACTION_TYPE_LABEL_MAP,
  IReferral,
  InteractionType,
  ReferralActor,
  ReferralStatus,
  StafferActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  isSameIdentifier,
  WithRef,
} from '@principle-theorem/shared';

@Injectable({
  providedIn: 'root',
})
export class ReferralInteractionService {
  private _snackBar = inject(MatSnackBar);

  async create(
    referral: WithRef<IReferral>,
    type: InteractionType,
    content: MixedSchema,
    actor: ReferralActor,
    metadata?: Record<string, string | number | boolean | undefined>
  ): Promise<void> {
    try {
      const interaction = ReferralInteraction.init({
        type,
        content,
        actor,
        metadata,
      });

      await Firestore.addDoc(ReferralInteraction.col(referral), interaction);
    } catch {
      this._snackBar.open('Error creating referral interaction');
    }
  }

  async createReferralCreatedInteraction(
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): Promise<void> {
    await this.create(
      referral,
      InteractionType.ReferralCreated,
      initVersionedSchema(
        INTERACTION_TYPE_LABEL_MAP[InteractionType.ReferralCreated]
      ),
      actor
    );
  }

  async createEmailSentToContactInteraction(
    referral: WithRef<IReferral>,
    referralStaffer: StafferActor,
    actor: ReferralActor,
    emailSubject?: string,
    emailContent?: MixedSchema
  ): Promise<void> {
    const content = this._buildEmailContent(
      referral,
      referralStaffer,
      actor,
      emailSubject,
      emailContent
    );

    await this.create(referral, InteractionType.Email, content, actor);
  }

  async createEmailSentToPatientInteraction(
    referral: WithRef<IReferral>,
    referralStaffer: StafferActor,
    actor: ReferralActor
  ): Promise<void> {
    const actorIsReferralStaffer = isSameIdentifier(actor, referralStaffer);
    const patient = referral.patient;
    const base = `Referral letter emailed to ${patient?.name || 'the patient'}`;

    const content = actorIsReferralStaffer
      ? `${base}.`
      : `${base} on behalf of ${referralStaffer.name}.`;

    await this.create(
      referral,
      InteractionType.Email,
      initVersionedSchema(content),
      actor
    );
  }

  async createStatusChangedInteraction(
    referral: WithRef<IReferral>,
    oldStatus: ReferralStatus,
    newStatus: ReferralStatus,
    actor: ReferralActor
  ): Promise<void> {
    await this.create(
      referral,
      InteractionType.ReferralStatusChanged,
      initVersionedSchema(`Status changed from ${oldStatus} to ${newStatus}`),
      actor,
      { oldStatus, newStatus }
    );
  }

  async createReadByInteraction(
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): Promise<void> {
    await this.create(
      referral,
      InteractionType.ReferralReadBy,
      initVersionedSchema(`Viewed the referral`),
      actor
    );
  }

  private _buildEmailContent(
    referral: WithRef<IReferral>,
    referralStaffer: StafferActor,
    actor: ReferralActor,
    emailSubject?: string,
    emailContent?: MixedSchema
  ): MixedSchema {
    const actorIsReferralStaffer = isSameIdentifier(actor, referralStaffer);
    const contact = Referral.getContact(referral);
    const recipientName = contact?.name || 'the recipient';

    const paragraphs: NodeSchema<BlockNodes.Paragraph>[] = [];

    const sentToText = actorIsReferralStaffer
      ? `Sent an email to ${recipientName}.`
      : `Sent an email to ${recipientName} on behalf of ${referralStaffer.name}.`;
    paragraphs.push(toParagraphContent(sentToText));
    paragraphs.push(toParagraphContent([]));

    if (emailSubject) {
      paragraphs.push(
        toParagraphContent([
          toTextContent('Subject: ', {}, [{ type: TextFormatting.Bold }]),
          toTextContent(emailSubject),
        ])
      );
      paragraphs.push(toParagraphContent([]));
    }

    if (emailContent) {
      const rawContent = getMixedContent(emailContent);
      if (rawContent.content) {
        for (const node of rawContent.content) {
          if (node.type === BlockNodes.Paragraph) {
            paragraphs.push(node as NodeSchema<BlockNodes.Paragraph>);
          }
        }
      }
    }

    return initVersionedSchema(paragraphs);
  }
}
